// Live navigation against the active route — port of TrailBivvy/App/Services/NavigationSession.swift: progress,
// remaining distance and ascent, ETA and off-route alerts. Works fully offline (GPS + the route geometry).
// Off-route alerts vibrate (where the browser supports it) and post a notification when the page is hidden and the
// hiker allowed notifications (decisions.md PWA-28).

import { signal, computed } from '@preact/signals';
import type { EpochMs, TrackPoint, TrailRoute, TrailStage } from '../core/models';
import {
  HikingTimeEstimator,
  OffRouteConfiguration,
  RouteProgressCalculator,
  RouteTrack,
  type RouteProgress,
} from '../nav';

export interface OffRouteAlerter {
  /** Called when the hiker left the route while navigating (distance in metres). */
  leftRoute(distance: number): void;
}

export class NavigationSession {
  readonly route = signal<TrailRoute | undefined>(undefined);
  readonly track = signal<RouteTrack | undefined>(undefined);
  readonly stages = signal<TrailStage[]>([]);
  readonly progress = signal<RouteProgress | undefined>(undefined);
  readonly lastFix = signal<TrackPoint | undefined>(undefined);
  /** Most recent off-route state for the in-app banner (cleared when back on route). */
  readonly isOffRoute = signal(false);
  /** True while the hiker explicitly navigates (higher GPS accuracy, alerts on). */
  readonly isActive = signal(false);
  readonly currentStage = computed(() => {
    const id = this.progress.value?.currentStageID;
    return id ? this.stages.value.find((s) => s.id === id) : undefined;
  });

  private calculator: RouteProgressCalculator | undefined;
  private offRouteConfig: OffRouteConfiguration;
  private estimatorValue: HikingTimeEstimator;

  constructor(
    private readonly now: () => EpochMs,
    offRouteConfiguration: OffRouteConfiguration,
    estimator: HikingTimeEstimator,
    private readonly alerter?: OffRouteAlerter,
  ) {
    this.offRouteConfig = offRouteConfiguration;
    this.estimatorValue = estimator;
  }

  get offRouteConfiguration(): OffRouteConfiguration {
    return this.offRouteConfig;
  }

  set offRouteConfiguration(value: OffRouteConfiguration) {
    this.offRouteConfig = value;
    this.rebuildCalculator();
  }

  get estimator(): HikingTimeEstimator {
    return this.estimatorValue;
  }

  /** Updated in place so position history and off-route state survive a pace recalibration. */
  set estimator(value: HikingTimeEstimator) {
    this.estimatorValue = value;
    if (this.calculator) this.calculator.estimator = value;
  }

  /** Sets the route to follow (whole trail by default, or selected/combined/reversed stages). */
  setRoute(route: TrailRoute, stages: TrailStage[]): void {
    this.route.value = route;
    this.stages.value = stages;
    this.track.value = new RouteTrack(route);
    this.rebuildCalculator();
    const fix = this.lastFix.value;
    if (fix) this.handle(fix);
  }

  /** Forgets the route ("Stop following this trail"); the last fix is kept (it is the hiker's position). */
  clearRoute(): void {
    this.stop();
    this.route.value = undefined;
    this.stages.value = [];
    this.track.value = undefined;
    this.progress.value = undefined;
    this.rebuildCalculator();
  }

  start(): void {
    this.isActive.value = true;
  }

  stop(): void {
    this.isActive.value = false;
    this.isOffRoute.value = false;
  }

  /** Processes a GPS fix (windowed projection: cheap enough for every fix). */
  handle(fix: TrackPoint): void {
    this.lastFix.value = fix;
    const calc = this.calculator;
    if (!calc) return;
    const result = calc.update(fix, this.now());
    this.progress.value = result.progress;
    if (result.event?.kind === 'leftRoute') {
      this.isOffRoute.value = true;
      if (this.isActive.value && this.offRouteConfig.isEnabled) this.alerter?.leftRoute(result.event.distance);
    } else if (result.event?.kind === 'returnedToRoute') {
      this.isOffRoute.value = false;
    }
  }

  /** Forgets the last fix so no stale position is presented as current. */
  clearFix(): void {
    this.lastFix.value = undefined;
    this.progress.value = undefined;
    this.isOffRoute.value = false;
    this.rebuildCalculator();
  }

  /** Progress at an arbitrary distance (elevation profile scrubbing, planning). */
  progressAt(distance: number): RouteProgress | undefined {
    return this.calculator?.progress(distance, this.now());
  }

  private rebuildCalculator(): void {
    // A fresh detector starts "on route": clear the banner so it cannot stay on forever.
    this.isOffRoute.value = false;
    const track = this.track.value;
    this.calculator = track ? new RouteProgressCalculator(track, this.stages.value, this.estimatorValue, this.offRouteConfig) : undefined;
  }
}
